import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "mb-8 text-center" }
const _hoisted_2 = { class: "d-flex justify-center align-center" }
const _hoisted_3 = {
  key: 0,
  class: "text-center"
}
const _hoisted_4 = {
  key: 1,
  class: "text-center"
}
const _hoisted_5 = { class: "position-relative" }
const _hoisted_6 = { class: "d-flex" }
const _hoisted_7 = { class: "text-body-1 font-weight-medium" }
const _hoisted_8 = { class: "d-flex flex-column" }
const _hoisted_9 = { class: "text-body-1 font-weight-medium" }
const _hoisted_10 = { class: "text-body-2 font-weight-medium" }
const _hoisted_11 = { class: "text-body-2" }
const _hoisted_12 = { class: "d-flex justify-space-between align-center" }
const _hoisted_13 = { class: "mt-2 d-flex justify-space-between align-center" }
const _hoisted_14 = { class: "text-body-2" }
const _hoisted_15 = { class: "d-flex align-center" }
const _hoisted_16 = { class: "text-caption" }
const _hoisted_17 = { class: "d-flex align-center" }

import { ref, computed, onMounted } from 'vue'
import { useDeviceStore } from './store'
import { Device } from './models/device'
import { useDeviceLoader } from '@/composables/useDeviceLoader'
import { useMeasurementsStore } from '@/views/DeviceSamples/store'
import type { DeviceSample } from '@/views/DeviceSamples/models/deviceSample'

// Store

export default /*@__PURE__*/_defineComponent({
  __name: 'DevicesView',
  setup(__props) {

const deviceStore = useDeviceStore()
const deviceLoader = useDeviceLoader()
const measurementsStore = useMeasurementsStore()

// Loading state
const loading = ref(false)
const loadingMeasurements = ref<Record<string, boolean>>({})

// Device samples por deviceId (para imagen y datos reales)
const deviceSamplesMap = ref<Record<string, DeviceSample[]>>({})

// Computed para obtener los dispositivos del store
const devices = computed(() => deviceStore.devices)

// Helper functions
const parseMetadata = (metadataStr: string): Record<string, unknown> => {
    try {
        return typeof metadataStr === 'string' ? JSON.parse(metadataStr) : {}
    } catch (e) {
        return {}
    }
}

const getDeviceImage = (device: Device): string => {
    // Buscar la última device sample con imageUrl
    const samples = deviceSamplesMap.value[device.id] || []
    const sampleWithImage = samples.find(s => s.imageUrl && s.imageUrl.trim() !== '')
    if (sampleWithImage) return sampleWithImage.imageUrl

    // Fallback a imágenes del device
    if (device.images && device.images.length > 0) {
        const sortedImages = device.images.sort((a, b) => a.position - b.position)
        return sortedImages[0].src
    }
    return '/images/2.png'
}

const getDeviceStatus = (device: Device) => {
    const metadata = parseMetadata(device.metadata)
    const status = (metadata.status as string | undefined) || (device.active ? 'connected' : 'disconnected')
    const statusMap: Record<string, { text: string; color: string }> = {
        'connected': { text: 'Conectado', color: '#689F38' },
        'disconnected': { text: 'Desconectado', color: '#C62828' },
        'sync_delay': { text: 'Retraso sincronización', color: '#FFC107' },
        'maintenance': { text: 'Mantenimiento', color: '#FF5722' }
    }
    return statusMap[status] || statusMap.disconnected
}

const getGrowthPercentage = (device: Device): number => {
    const samples = deviceSamplesMap.value[device.id] || []
    // Última sample con growthPercent válido
    const lastWithGrowth = samples.find(s => s.managed && s.growthPercent != null)
    return lastWithGrowth ? lastWithGrowth.growthPercent : 0
}

const getExpectedMeasurements = (device: Device) => {
    const samples = deviceSamplesMap.value[device.id] || []
    const actual = samples.length
    const metadata = parseMetadata(device.metadata)
    const expected = (metadata.expectedMeasurements as number | undefined) || 0
    return { actual, expected }
}

const getCompletionPercentage = (device: Device): number => {
    const samples = deviceSamplesMap.value[device.id] || []
    const managed = samples.filter(s => s.managed).length
    const total = samples.length
    if (total === 0) return 0
    return Math.round((managed / total) * 100)
}

const getLastUpdate = (device: Device): string => {
    const samples = deviceSamplesMap.value[device.id] || []
    if (samples.length === 0) return 'No disponible'
    // Ordenar por time desc y tomar la más reciente
    const sorted = [...samples].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
    return new Intl.DateTimeFormat('es-CL', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit'
    }).format(new Date(sorted[0].time))
}

const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
    }).format(new Date(date))
}

const getTotalUpdates = (device: Device): number => {
    const samples = deviceSamplesMap.value[device.id] || []
    return samples.length
}

const getObservations = (device: Device): string => {
    const samples = deviceSamplesMap.value[device.id] || []
    const lastWithObs = samples.find(s => s.observation && s.observation.trim() !== '')
    return lastWithObs ? lastWithObs.observation : 'Sin observaciones'
}

// Función para cargar device samples de un dispositivo específico
const loadDeviceSamples = async (deviceId: string) => {
    if (loadingMeasurements.value[deviceId]) return

    loadingMeasurements.value[deviceId] = true
    try {
        const samples = await measurementsStore.getMeasurementsByDeviceId(deviceId)
        deviceSamplesMap.value[deviceId] = samples
    } finally {
        loadingMeasurements.value[deviceId] = false
    }
}

// Lifecycle
onMounted(async () => {
    loading.value = true
    try {
        // Usar carga condicional por rol
        await deviceLoader.loadDevicesByRole()

        // Cargar device samples para todos los dispositivos
        const samplePromises = devices.value.map(device =>
            loadDeviceSamples(device.id)
        )
        await Promise.all(samplePromises)

    } catch (err) {
        console.error('Error loading devices view:', err)
    } finally {
        loading.value = false
    }
})

return (_ctx: any,_cache: any) => {
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_img = _resolveComponent("v-img")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_subtitle = _resolveComponent("v-card-subtitle")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_item = _resolveComponent("v-card-item")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, { fluid: "" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _cache[1] || (_cache[1] = _createElementVNode("h1", { class: "text-h2 font-weight-bold primary--text mb-2" }, "Dispositivos", -1)),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_v_divider, {
            class: "mx-4",
            width: "50"
          }),
          _cache[0] || (_cache[0] = _createElementVNode("span", { class: "text-subtitle-1 text-grey" }, "Monitoreo y control", -1)),
          _createVNode(_component_v_divider, {
            class: "mx-4",
            width: "50"
          })
        ]),
        _cache[2] || (_cache[2] = _createElementVNode("p", { class: "text-body-1 mt-4 text-grey-darken-1 max-width-800 mx-auto" }, " Gestione sus dispositivos de medición conectados. Revise el estado, visualice datos en tiempo real. ", -1))
      ]),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_v_progress_circular, {
              indeterminate: "",
              color: "primary",
              size: "64"
            }),
            _cache[3] || (_cache[3] = _createElementVNode("p", { class: "mt-4 text-body-1" }, "Cargando dispositivos...", -1))
          ]))
        : (devices.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_component_v_icon, {
                size: "64",
                color: "grey-lighten-1"
              }, {
                default: _withCtx(() => _cache[4] || (_cache[4] = [
                  _createTextVNode("mdi-devices")
                ])),
                _: 1
              }),
              _cache[5] || (_cache[5] = _createElementVNode("p", { class: "mt-4 text-h6 text-grey" }, "No hay dispositivos registrados", -1)),
              _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-body-2 text-grey" }, "Los dispositivos aparecerán aquí una vez que se registren en el sistema.", -1))
            ]))
          : (_openBlock(), _createBlock(_component_v_row, { key: 2 }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(devices.value, (device) => {
                  return (_openBlock(), _createBlock(_component_v_col, {
                    cols: "12",
                    md: "4",
                    key: device.id
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card, {
                        class: "rounded-lg overflow-hidden",
                        elevation: "3"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_5, [
                            _createVNode(_component_v_img, {
                              src: getDeviceImage(device),
                              height: "250",
                              cover: "",
                              class: "gradient-overlay"
                            }, {
                              placeholder: _withCtx(() => [
                                _createVNode(_component_v_row, {
                                  class: "fill-height ma-0",
                                  align: "center",
                                  justify: "center"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_progress_circular, {
                                      indeterminate: "",
                                      color: "grey-lighten-5"
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 2
                            }, 1032, ["src"]),
                            _createVNode(_component_v_avatar, {
                              size: "80",
                              class: "avatar-overlap"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_avatar, {
                                  color: "#E8F5E9",
                                  size: "72"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_icon, {
                                      color: "#4CAF50",
                                      size: "36"
                                    }, {
                                      default: _withCtx(() => _cache[7] || (_cache[7] = [
                                        _createTextVNode("mdi-sprout")
                                      ])),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _createVNode(_component_v_card_item, { class: "pt-8 mt-6" }, {
                            append: _withCtx(() => [
                              _createVNode(_component_v_btn, {
                                icon: "",
                                to: `/devices/${device.id}`
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, null, {
                                    default: _withCtx(() => _cache[8] || (_cache[8] = [
                                      _createTextVNode("mdi-arrow-right")
                                    ])),
                                    _: 1
                                  })
                                ]),
                                _: 2
                              }, 1032, ["to"])
                            ]),
                            default: _withCtx(() => [
                              _createVNode(_component_v_card_title, { class: "text-h6 font-weight-bold px-0" }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(device.name), 1)
                                ]),
                                _: 2
                              }, 1024),
                              _createVNode(_component_v_card_subtitle, { class: "px-0" }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(device.extendedName), 1)
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_card_text, { class: "px-4 pb-0" }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_6, [
                                _createVNode(_component_v_sheet, { class: "pa-3 rounded bg-grey-lighten-4 flex-grow-1 mr-2" }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_7, _toDisplayString(getGrowthPercentage(device)) + "%", 1),
                                    _cache[9] || (_cache[9] = _createElementVNode("div", { class: "text-caption text-grey" }, "Crecimiento", -1))
                                  ]),
                                  _: 2
                                }, 1024),
                                _createVNode(_component_v_sheet, { class: "pa-3 rounded bg-grey-lighten-4 flex-grow-1" }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_8, [
                                      _createElementVNode("div", _hoisted_9, "Fecha inicio: " + _toDisplayString(formatDate(device.initDate)), 1),
                                      _createElementVNode("div", _hoisted_10, "Última actualización: " + _toDisplayString(getLastUpdate(device)), 1)
                                    ])
                                  ]),
                                  _: 2
                                }, 1024)
                              ])
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_card_text, { class: "pt-3" }, {
                            default: _withCtx(() => [
                              _createElementVNode("p", _hoisted_11, _toDisplayString(getObservations(device)), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_divider),
                          _createVNode(_component_v_card_text, null, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_12, [
                                _createElementVNode("div", null, "Periodicidad: " + _toDisplayString(device.periodicity) + " min", 1),
                                _createVNode(_component_v_chip, {
                                  color: getDeviceStatus(device).color,
                                  "text-color": "white",
                                  size: "small",
                                  class: "rounded px-2"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(getDeviceStatus(device).text), 1)
                                  ]),
                                  _: 2
                                }, 1032, ["color"])
                              ]),
                              _createElementVNode("div", _hoisted_13, [
                                _createElementVNode("div", null, "Mediciones: " + _toDisplayString(getExpectedMeasurements(device).actual) + "/" + _toDisplayString(getExpectedMeasurements(device).expected), 1),
                                _createElementVNode("div", _hoisted_14, _toDisplayString(getCompletionPercentage(device)) + "% Completado", 1)
                              ]),
                              _createVNode(_component_v_progress_linear, {
                                "model-value": getCompletionPercentage(device),
                                height: "8",
                                rounded: "",
                                color: getCompletionPercentage(device) >= 70 ? 'purple' : 'primary',
                                class: "mt-1"
                              }, null, 8, ["model-value", "color"])
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_card_text, { class: "d-flex justify-space-between pt-0" }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_15, [
                                _createVNode(_component_v_icon, {
                                  color: device.active ? 'green' : 'grey',
                                  size: "small",
                                  class: "mr-1"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(device.active ? 'mdi-check-circle' : 'mdi-pause-circle'), 1)
                                  ]),
                                  _: 2
                                }, 1032, ["color"]),
                                _createElementVNode("span", _hoisted_16, _toDisplayString(device.active ? 'Activo' : 'Inactivo'), 1)
                              ]),
                              _createElementVNode("div", _hoisted_17, [
                                _cache[11] || (_cache[11] = _createElementVNode("span", { class: "mr-2" }, "Actualizaciones", -1)),
                                _createVNode(_component_v_icon, {
                                  size: "small",
                                  class: "mr-1"
                                }, {
                                  default: _withCtx(() => _cache[10] || (_cache[10] = [
                                    _createTextVNode("mdi-message-outline")
                                  ])),
                                  _: 1
                                }),
                                _createElementVNode("span", null, _toDisplayString(getTotalUpdates(device)), 1)
                              ])
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1024))
                }), 128))
              ]),
              _: 1
            }))
    ]),
    _: 1
  }))
}
}

})